import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import socket from '../../utils/socket';
import './Registration.scss';

const Registration = () => {
    const { codePartie } = useParams();
    const navigate = useNavigate();

    const [tours, setTours] = useState(() => {
        const storedTours = sessionStorage.getItem(`gameTours_${codePartie}`) || localStorage.getItem(`gameTours_${codePartie}`);
        return storedTours ? parseInt(storedTours) : 10;
    });
    const [players, setPlayers] = useState([]);
    const [copied, setCopied] = useState(false);

    // 1. On lit le sessionStorage UNE SEULE FOIS au démarrage pour éviter les boucles infinies
    const [currentPlayer] = useState(() => {
        const storedPlayer = sessionStorage.getItem(`currentPlayer_${codePartie}`) || localStorage.getItem(`currentPlayer_${codePartie}`);
        return storedPlayer ? JSON.parse(storedPlayer) : null;
    });

    // Sécurité : On lit directement l'information transmise par le serveur
    const isCreator = currentPlayer && currentPlayer.isCreator === true;

    useEffect(() => {
        // Redirection de sécurité immédiate
        if (!currentPlayer) {
            navigate('/');
            return;
        }

        const onConnect = () => {
            socket.emit('rejoindre_partie', codePartie);
            socket.emit('demande_liste_joueurs', codePartie, (playersList, toursVal) => {
                if (playersList) setPlayers(playersList);
                if (toursVal) setTours(toursVal);
            });
        };

        const onListeJoueurs = (playersList) => {
            setPlayers(playersList);
        };

        const onToursMisAJour = (toursVal) => {
            setTours(toursVal);
        };

        const onPartieLancee = (data) => {
            // La partie démarre : on sauvegarde les infos
            const safeCode = (data && data.codePartie) ? data.codePartie : codePartie;
            if (data) {
                sessionStorage.setItem(`gameData_${safeCode}`, JSON.stringify(data));
            }
            
            // Pour les autres joueurs (B, C, D) : basculer directement dans la même fenêtre
            if (!isCreator) {
                navigate(`/game/${safeCode}`);
            }
        };

        const onPartieAnnulee = (data) => {
            alert(data?.message || "La partie a été annulée car un joueur s'est déconnecté.");
            sessionStorage.removeItem(`currentPlayer_${codePartie}`);
            localStorage.removeItem(`currentPlayer_${codePartie}`);
            sessionStorage.removeItem(`gameData_${codePartie}`);
            navigate('/');
        };

        // Branchement
        if (socket.connected) {
            onConnect();
        } else {
            socket.on('connect', onConnect);
        }

        socket.on('liste_joueurs', onListeJoueurs);
        socket.on('tours_mis_a_jour', onToursMisAJour);
        socket.on('partie_lancee', onPartieLancee);
        socket.on('partie_annulee', onPartieAnnulee);

        // Nettoyage rigoureux
        return () => {
            socket.off('connect', onConnect);
            socket.off('liste_joueurs', onListeJoueurs);
            socket.off('tours_mis_a_jour', onToursMisAJour);
            socket.off('partie_lancee', onPartieLancee);
            socket.off('partie_annulee', onPartieAnnulee);
        };
    }, [codePartie, navigate, currentPlayer, isCreator]);

    const demarrerPartie = () => {
        if (players.length < 2) {
            alert("Il faut au moins 2 joueurs connectés pour démarrer !");
            return;
        }

        // Sauvegarder les données de jeu
        const gameData = {
            codePartie,
            tours,
            players
        };
        sessionStorage.setItem(`gameData_${codePartie}`, JSON.stringify(gameData));

        // Avertir le serveur pour lancer la partie chez tous les joueurs
        if (socket) {
            socket.emit('lancer_jeu_serveur', { codePartie });
        }

        // Basculer directement dans la même fenêtre pour l'hôte
        navigate(`/game/${codePartie}`);
    };

    const handleQuitterPartie = () => {
        if (socket) {
            socket.emit('quitter_partie', { codePartie });
        }
        sessionStorage.removeItem(`currentPlayer_${codePartie}`);
        localStorage.removeItem(`currentPlayer_${codePartie}`);
        sessionStorage.removeItem(`gameData_${codePartie}`);
        sessionStorage.removeItem(`gameTours_${codePartie}`);
        localStorage.removeItem(`gameTours_${codePartie}`);
        navigate('/');
    };

    const copierCodeSMS = () => {
        if (codePartie) {
            navigator.clipboard.writeText(codePartie);
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
        }
    };

    return (
        <div className="registration-container">
            <header className="hero-header">
                <h1>Memozyn Quizz</h1>
                <h2>Salle d'attente de la partie</h2>
            </header>

            <main className="config-main">
                <div className="partie-info">
                    <span>CODE DE LA PARTIE</span>
                    <strong className="code-display">{codePartie}</strong>
                    {codePartie && (
                        <div style={{ marginTop: '12px' }}>
                            <button
                                onClick={copierCodeSMS}
                                style={{
                                    background: copied ? '#2ecc71' : '#3498db',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {copied ? "✅ Code copié !" : "📋 Copier le code à envoyer par SMS"}
                            </button>
                        </div>
                    )}
                </div>

                {/* Affichage fixe du nombre de tours choisi par le créateur */}
                <div className="tours-selection-card">
                    <div className="tours-info-left">
                        <span className="tours-icon">⏱️</span>
                        <span className="tours-title">Nombre de tours :</span>
                    </div>
                    <span className="tours-badge">{tours} tours</span>
                </div>

                <div className="players-section">
                    <h3>Joueurs inscrits ({players.length}/4)</h3>
                    <div className="players-list">
                        {players.map(p => (
                            <div
                                key={p.id}
                                className={`player-card ${currentPlayer && String(currentPlayer.id) === String(p.id) ? 'current-player-card' : ''}`}
                                style={{ borderLeft: `8px solid ${p.color}` }}
                            >
                                <div className="color-bubble" style={{ backgroundColor: p.color }}></div>
                                <span className="player-name-lobby">
                                    {p.name} {currentPlayer && String(currentPlayer.id) === String(p.id) && " (Vous)"}
                                </span>
                                <div className="status-badge online">Prêt</div>
                            </div>
                        ))}
                        {Array.from({ length: Math.max(0, 4 - players.length) }).map((_, idx) => (
                            <div key={`empty-${idx}`} className="player-card empty-card">
                                <span className="player-name-lobby empty">En attente d'un joueur...</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="cta-section">
                    {isCreator ? (
                        <button
                            className="cta-button primary start"
                            onClick={demarrerPartie}
                            disabled={players.length < 2}
                        >
                            LANCER LA PARTIE 🚀
                        </button>
                    ) : (
                        <div className="waiting-message">
                            En attente du créateur de la partie pour la lancer...
                        </div>
                    )}

                    <div style={{ marginTop: '16px' }}>
                        <button
                            onClick={handleQuitterPartie}
                            style={{
                                background: 'transparent',
                                border: '1px solid rgba(255, 255, 255, 0.25)',
                                color: '#e74c3c',
                                padding: '10px 20px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.95rem',
                                fontWeight: '600',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {isCreator ? "❌ Annuler la partie et revenir à l'accueil" : "🚪 Quitter la salle d'attente"}
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Registration;